import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Event,
  NavigationEnd,
  NavigationStart,
  Router
} from '@angular/router';
import { User } from '@core/models';
import { GeoData } from '@core/models/country.model';
import { SessionModelResponse } from '@core/models/session-model-response.model';
import { SettingsModel } from '@core/models/settings.model';
import {
  AbTestsService,
  API_LIST,
  appData,
  AppsflyerService,
  CartService,
  CountriesService,
  GonativeService,
  MessagePopupService,
  PageTplDataModel,
  PageTplDataService,
  PosthogService,
  ProjectDataService,
  UserDrawTicketCountersService,
  UserengageService,
  UserTrackingService,
  VersionControlService
} from '@core/services';
import { SettingsService } from '@core/services/settings/settings.service';
import { Select, Store } from '@ngxs/store';
import {
  ClearAuthData,
  RefreshUser,
  SetSession
} from '@store/auth/auth.actions';
import { AuthState } from '@store/auth/auth.state';
import { ClearCartErrors } from '@store/cart/cart.actions';
import {
  GetCategories,
  SetActiveProperty
} from '@store/settings/settings.actions';
import { SettingsState } from '@store/settings/settings.state';
import { Observable } from 'rxjs';
import { ProjectDataModel } from '../../../../../projects/project-data.default';

@Injectable({
  providedIn: 'root'
})
export class InitService {
  public pageTplData: PageTplDataModel;
  firstTimeLaunched = false;
  subWebsite = 'website-p7d';
  public websiteStatus = '1';
  public websiteDisabledMessage = '';

  @Select(AuthState.user) user$: Observable<User>;
  @Select(SettingsState.settings) settings$: Observable<SettingsModel>;

  constructor(
    public pageTplDataService: PageTplDataService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private versionControlService: VersionControlService,
    private meta: Meta,
    private window: Window,
    private userTracking: UserTrackingService,
    private store: Store,
    private httpClient: HttpClient,
    private ticketCounters: UserDrawTicketCountersService,
    private cartService: CartService,
    private settingsService: SettingsService,
    private userEngageService: UserengageService,
    private gonative: GonativeService,
    private appsFlyer: AppsflyerService,
    public messagePopupService: MessagePopupService,
    private countriesService: CountriesService,
    private projectDataService: ProjectDataService,
    private abTestsService: AbTestsService,
    private posthogService: PosthogService
  ) {}

  init(options: { projectData: ProjectDataModel }): void {
    this.projectDataService.setData(options.projectData);
    this.posthogService.init(
      options.projectData.posthogKey,
      options.projectData.posthogHost
    );
    this.userTracking.appendHeadScripts();
    this.appsFlyer.initEventRecorderForGtm();
    this.purgeServiceWorker();
    this.countriesService.detectGeoCountryIfExpired();

    this.countriesService.visitorCountry$.subscribe((geoData: GeoData) => {
      if (geoData) {
        if (geoData.restricted) {
          this.router.navigate(['/restricted-country']);
        }
      }
    });

    this.abTestsService.init();

    this.settings$.subscribe((settings: SettingsModel) => {
      if (!settings) {
        return;
      }

      this.websiteStatus = String(settings.website_status);
      this.websiteDisabledMessage = settings.website_disabled_message;
    });

    this.window.addEventListener('message', (event: any) => {
      if (event.data !== 'is_iframe') {
        return;
      }
      this.updateAllLinksToParent();
    });

    this.store.dispatch(new RefreshUser());

    this.user$.subscribe(user => {
      if (user && this.posthogService.getDistinctId() !== String(user.id)) {
        this.posthogService.identify(`${user.id}`, {
          email: user.email,
          username: user.username,
          name: `${user.first_name} ${user.last_name}`
        });
      }
    });

    this.pageTplData = this.pageTplDataService.pageTplData;

    this.userEngageService.installPageListener();
    this.gonative.installIfNative();

    this.store.dispatch(new SetActiveProperty());
    this.store.dispatch(new GetCategories());

    // find active route data
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        appData.navigationTrigger = event.navigationTrigger;
      }

      if (event instanceof NavigationEnd) {
        this.firstTimeLaunched = true;

        const routeSnapshot: ActivatedRouteSnapshot[] = [];
        const activeRoutes: ActivatedRoute[] = this.activatedRoute.children;

        activeRoutes.forEach((route: ActivatedRoute) => {
          let activeRoute: ActivatedRoute = route;
          while (activeRoute.firstChild) {
            activeRoute = activeRoute.firstChild;
          }
          routeSnapshot.push(activeRoute.snapshot);
        });
        // save data for later use in other components
        this.pageTplDataService.setPageTitle(
          (<any>routeSnapshot[0].data).pageTitle
        );
        this.pageTplDataService.setPageClass(
          (<any>routeSnapshot[0].data).pageClass
        );
        this.subWebsite = this.getSubWebsiteFromActiveRouteSnapshot(
          routeSnapshot[0]
        );
        this.updateMetaTags(this.subWebsite);
        this.store.dispatch(new ClearCartErrors());
        this.countriesService.detectGeoCountryIfExpired();

        this.messagePopupService.clearMessages();
        this.handleQueryParamSessions(routeSnapshot);
        this.window.scrollTo(0, 0);
        appData.navigationTrigger = '';
      }

      this.versionControlService.reloadPageOnceChunkLoadingFailed(event);
    });
  }

  handleQueryParamSessions(routeSnapshot: ActivatedRouteSnapshot[]) {
    let queryParamsWithoutSession = { ...routeSnapshot[0].queryParams };
    if (queryParamsWithoutSession.session_token)
      delete queryParamsWithoutSession.session_token;
    if (queryParamsWithoutSession.user_session)
      delete queryParamsWithoutSession.user_session;
    if (queryParamsWithoutSession.redirect_url)
      delete queryParamsWithoutSession.redirect_url;

    // if external login requested, init user data
    if (routeSnapshot[0].queryParams['user_session']) {
      this.store.dispatch(new ClearAuthData());

      this.httpClient
        .post(
          API_LIST.loginExternal(routeSnapshot[0].queryParams['user_session']),
          {}
        )
        .subscribe(
          (session: SessionModelResponse) => {
            this.store.dispatch(new SetSession(session));
            this.ticketCounters.clearCounters();

            if (session.user.ab_test_seed) {
              this.abTestsService.setSeed(session.user.ab_test_seed);
            }

            this.router.navigate(
              [this.getTargetUrl(routeSnapshot[0].queryParams['redirect_url'])],
              {
                queryParams: queryParamsWithoutSession
              }
            );
          },
          err => {
            queryParamsWithoutSession.redirect_url = this.getTargetUrl(
              routeSnapshot[0].queryParams['redirect_url']
            );

            this.router.navigate(['/my-account'], {
              queryParams: queryParamsWithoutSession
            });
          }
        );
    }

    // if active session is found, init user data
    if (routeSnapshot[0].queryParams['session_token']) {
      this.store.dispatch(new ClearAuthData());

      this.httpClient
        .get(
          API_LIST.getSessionData(routeSnapshot[0].queryParams['session_token'])
        )
        .subscribe(
          (session: SessionModelResponse) => {
            this.store.dispatch(new SetSession(session));
            this.ticketCounters.clearCounters();

            this.router.navigate(
              [this.getTargetUrl(routeSnapshot[0].queryParams['redirect_url'])],
              {
                queryParams: queryParamsWithoutSession
              }
            );
          },
          err => {
            queryParamsWithoutSession.redirect_url = this.getTargetUrl(
              routeSnapshot[0].queryParams['redirect_url']
            );

            this.router.navigate(['/my-account'], {
              queryParams: queryParamsWithoutSession
            });
          }
        );
    }
  }

  updateAllLinksToParent() {
    let links = (<any>document).getElementsByTagName('a');

    for (let link of links) {
      if (!link.target && link.getAttribute('href')) {
        link.target = '_top';

        // to remove all listeners, including angular routerLink
        let clonedLink = link.cloneNode(true);
        link.parentNode.replaceChild(clonedLink, link);
      }
    }
  }

  getSubWebsiteFromActiveRouteSnapshot(
    routeSnapshot: ActivatedRouteSnapshot | { data: any; parent: any }
  ) {
    if (routeSnapshot.data.website) {
      return this.getSubWebsite(routeSnapshot.data.website);
    }

    if (routeSnapshot.parent) {
      return this.getSubWebsiteFromActiveRouteSnapshot(routeSnapshot.parent);
    }

    return this.getSubWebsite(null);
  }

  getSubWebsite(website) {
    if (website) {
      return 'website-' + website;
    } else {
      return 'website-p7d';
    }
  }

  updateMetaTags(websiteString: string) {
    this.meta.updateTag({
      property: 'fb:app_id',
      content: (<any>this.window).ENVIRONMENT_PARAMETERS.fb_app_id
    });
  }

  getTargetUrl(url) {
    if (!url) {
      url = this.window.location.pathname;
    } else {
      url = decodeURIComponent(url);
    }

    return url;
  }

  purgeServiceWorker() {
    if ('caches' in window) {
      caches
        .keys()
        .then(function (keyList) {
          return Promise.all(
            keyList.map(function (key) {
              return caches.delete(key);
            })
          );
        })
        .catch(() => {});
    }

    if (window.navigator && navigator.serviceWorker) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (let registration of registrations) {
          registration.unregister();
        }
      });
    }
  }
}
